import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Download,
  Code2,
  Terminal,
  Puzzle,
  ExternalLink,
  Check,
  Copy,
  Sparkles,
  Zap,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/layout/Header";

export default function Extensions() {
  const [copied, setCopied] = useState(null);

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(null), 2000);
  };

  const handleDownloadVSCode = () => {
    // In production, this would link to the actual .vsix file or marketplace
    toast.success("Download started! Install the .vsix file in VS Code.");
    window.open("/downloads/diagramai-vscode-1.0.0.vsix", "_blank");
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-b from-slate-50 to-white py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
            <Puzzle className="h-4 w-4" />
            Extensions & Integrations
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 font-heading tracking-tight">
            Bring DiagramFoundry to your workflow
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto font-body">
            Generate diagrams from your code directly in VS Code, from the command line,
            or integrate with your favorite tools via our API.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <Tabs defaultValue="vscode" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 mb-12">
            <TabsTrigger value="vscode" data-testid="vscode-tab">
              <Code2 className="h-4 w-4 mr-2" />
              VS Code
            </TabsTrigger>
            <TabsTrigger value="cli" data-testid="cli-tab">
              <Terminal className="h-4 w-4 mr-2" />
              CLI
            </TabsTrigger>
            <TabsTrigger value="api" data-testid="api-tab">
              <Zap className="h-4 w-4 mr-2" />
              API
            </TabsTrigger>
          </TabsList>

          {/* VS Code Extension */}
          <TabsContent value="vscode">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  VS Code Extension
                </h2>
                <p className="text-slate-600 mb-6">
                  Generate beautiful diagrams from your code with a single keyboard shortcut.
                  Our AI analyzes your code and creates the perfect diagram automatically.
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    "Auto-detect language and diagram type",
                    "Support for Python, JavaScript, TypeScript, SQL, and more",
                    "Live preview in VS Code side panel",
                    "Generate from file or selection",
                    "Keyboard shortcut: Ctrl+Shift+D",
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-700">
                      <Check className="h-5 w-5 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button size="lg" onClick={handleDownloadVSCode} data-testid="download-vscode-btn">
                    <Download className="h-4 w-4 mr-2" />
                    Download .vsix
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => window.open("https://marketplace.visualstudio.com/items?itemName=diagramai.diagramai-vscode", "_blank")}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View on Marketplace
                  </Button>
                </div>

                <p className="text-sm text-slate-500 mt-4">
                  Requires VS Code 1.95.0 or later
                </p>
              </div>

              {/* Preview Image */}
              <div className="bg-slate-900 rounded-xl p-4 shadow-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-slate-400 text-xs ml-2">extension.ts</span>
                </div>
                <pre className="text-sm text-slate-300 font-mono leading-relaxed overflow-x-auto">
{`class Animal {
  name: string;
  speak(): void {}
}

class Dog extends Animal {
  speak() { return "Bark!"; }
}

class Cat extends Animal {
  speak() { return "Meow!"; }
}

// Press Ctrl+Shift+D to generate diagram →`}
                </pre>
                <div className="mt-4 p-3 bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <Sparkles className="h-4 w-4" />
                    Generated: Class Diagram
                  </div>
                </div>
              </div>
            </div>

            {/* Installation Steps */}
            <div className="mt-16 bg-slate-50 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-6">Quick Start</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    step: "1",
                    title: "Install Extension",
                    desc: "Download the .vsix file and install via VS Code's 'Install from VSIX' command.",
                  },
                  {
                    step: "2",
                    title: "Add API Key",
                    desc: "Open Settings, search for 'DiagramFoundry', and paste your API key from the dashboard.",
                  },
                  {
                    step: "3",
                    title: "Generate Diagrams",
                    desc: "Open any code file and press Ctrl+Shift+D to generate a diagram instantly!",
                  },
                ].map((item) => (
                  <div key={item.step} className="text-center">
                    <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-3">
                      {item.step}
                    </div>
                    <h4 className="font-medium text-slate-900 mb-2">{item.title}</h4>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* CLI Tool */}
          <TabsContent value="cli">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                  Coming Soon
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Command Line Interface
                </h2>
                <p className="text-slate-600">
                  Generate diagrams from your terminal. Perfect for CI/CD pipelines and automation.
                </p>
              </div>

              <div className="bg-slate-900 rounded-xl p-6 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-400 text-sm">Preview Commands</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                    onClick={() => handleCopy("npm install -g @diagramai/cli", "cli-install")}
                  >
                    {copied === "cli-install" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <pre className="text-sm text-green-400 font-mono">
{`# Install globally
npm install -g @diagramai/cli

# Generate diagram from file
diagramai generate src/models/user.py -o diagram.svg

# Generate from stdin
cat schema.sql | diagramai generate --type erd

# Watch mode for live updates
diagramai watch src/ --output docs/diagrams/`}
                </pre>
              </div>

              <div className="text-center">
                <p className="text-slate-600 mb-4">
                  Get notified when the CLI is available:
                </p>
                <div className="flex max-w-md mx-auto gap-2">
                  <Input placeholder="your@email.com" type="email" />
                  <Button>Notify Me</Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* API */}
          <TabsContent value="api">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  REST API
                </h2>
                <p className="text-slate-600">
                  Integrate diagram generation into your applications, documentation tools, or workflows.
                </p>
              </div>

              <div className="space-y-6">
                {/* Endpoint */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 mb-3">Generate Diagram</h3>
                  <div className="bg-slate-900 rounded-lg p-4 mb-4">
                    <code className="text-green-400 text-sm">
                      POST /api/diagrams/generate-from-source
                    </code>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    Send DSL code, natural language descriptions, or source code to generate diagrams.
                  </p>
                  <Button variant="outline" onClick={() => window.open("/dashboard", "_blank")}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Full Documentation
                  </Button>
                </div>

                {/* Example */}
                <div className="bg-slate-900 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-slate-400 text-sm">Example Request</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-white"
                      onClick={() => handleCopy(`curl -X POST "${window.location.origin}/api/diagrams/generate-from-source" \\
  -H "X-API-Key: your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"content": "class User { id, name, email }", "source_type": "source_code"}'`, "api-example")}
                    >
                      {copied === "api-example" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <pre className="text-sm text-slate-300 font-mono whitespace-pre-wrap">
{`curl -X POST "${window.location.origin}/api/diagrams/generate-from-source" \\
  -H "X-API-Key: your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "content": "class User { id, name, email }",
    "source_type": "source_code",
    "language": "javascript"
  }'`}
                  </pre>
                </div>

                {/* Response */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 mb-3">Response</h3>
                  <pre className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-700 overflow-x-auto">
{`{
  "diagram_id": "abc123",
  "dsl_code": "class\\n  class User {...}",
  "diagram_type": "class",
  "embed_url": "/embed/abc123",
  "iframe_code": "<iframe src='.../embed/abc123' ...></iframe>"
}`}
                  </pre>
                </div>

                <div className="text-center pt-6">
                  <Button size="lg" onClick={() => window.open("/dashboard", "_blank")}>
                    Get Your API Key
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
